import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Badge,
  useColorModeValue,
  useToast,
  Spinner,
  Center,
  SimpleGrid,
  Alert,
  AlertIcon,
  Icon,
  IconButton,
  StackDivider,
} from '@chakra-ui/react';
import {
  FaArrowLeft,
  FaEuroSign,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaEdit,
} from 'react-icons/fa';
import BazarGallery from '../components/Bazar/BazarGallery';
import { fetchBazarById } from '../utils/bazarApi';
import { useAuth } from '../contexts/AuthContext';

// Helper per ottenere il colore del badge stato
const getStatoColor = (stato) => {
  const statoColors = {
    disponibile: 'green',
    prenotato: 'yellow',
    venduto: 'gray',
    terminato: 'gray',
  };
  return statoColors[stato] || 'gray';
};

// Helper per ottenere il testo del badge stato
const getStatoLabel = (stato) => {
  const statoLabels = {
    disponibile: 'Disponibile',
    prenotato: 'Prenotato',
    venduto: 'Venduto',
    terminato: 'Terminato',
  };
  return statoLabels[stato] || stato;
};

// Helper per ottenere il nome del socio venditore
const getVenditoreInfo = (bazar) => {
  // Prova diverse strutture possibili per il socio
  // Strapi v5 può restituire il socio in diverse strutture:
  // - attributes.socio.data (relazione popolata)
  // - attributes.socio (relazione popolata direttamente)
  // - socio.data o socio (se non c'è attributes)
  let socio = null;

  if (bazar.attributes?.socio?.data) {
    socio = bazar.attributes.socio.data;
  } else if (bazar.attributes?.socio) {
    socio = bazar.attributes.socio;
  } else if (bazar.socio?.data) {
    socio = bazar.socio.data;
  } else if (bazar.socio) {
    socio = bazar.socio;
  }

  if (socio) {
    // Il socio può avere i campi direttamente o dentro attributes
    const nomeSocio = socio.nomeSocio || socio.attributes?.nomeSocio || '';
    const cognomeSocio =
      socio.cognomeSocio || socio.attributes?.cognomeSocio || '';
    const nomeCompleto = `${nomeSocio} ${cognomeSocio}`.trim();

    return {
      nome: nomeCompleto || 'Socio CIL',
      email: socio.email || socio.attributes?.email || null,
      telefono: socio.telefono || socio.attributes?.telefono || null,
      tesseramenti:
        socio.tesseramenti ||
        socio.attributes?.tesseramenti?.data ||
        socio.attributes?.tesseramenti ||
        [],
    };
  }
  return { nome: 'Socio CIL', email: null, telefono: null, tesseramenti: [] };
};

export default function BazarDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { userData, isAuthenticated } = useAuth();

  const [bazar, setBazar] = useState(null);
  const [loading, setLoading] = useState(true);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const loadBazar = async () => {
    try {
      setLoading(true);
      const data = await fetchBazarById(id);
      setBazar(data);
    } catch (error) {
      toast({
        title: 'Errore',
        description: error.message || "Impossibile caricare l'oggetto",
        status: 'error',
        duration: 3000,
      });
      navigate('/bazar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBazar();
  }, [id]);

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Center py={12}>
          <Spinner size="xl" color="brand.100" />
        </Center>
      </Container>
    );
  }

  if (!bazar) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          Oggetto non trovato
        </Alert>
      </Container>
    );
  }

  const bazarData = bazar.attributes || bazar;
  const stato = bazarData.stato || 'disponibile';
  const titolo = bazarData.titolo || 'Senza titolo';
  const descrizione = bazarData.descrizione || '';
  const {
    prezzo,
    categoria,
    condizione,
    prezzoNegoziabile,
    preferenzeContatto,
  } = bazarData;

  const venditoreInfo = getVenditoreInfo(bazar);

  const telefono =
    venditoreInfo.tesseramenti.length > 0
      ? venditoreInfo.tesseramenti[0].telefono || venditoreInfo.telefono
      : venditoreInfo.telefono;

  const isPrenotato = stato === 'prenotato';
  const isVendutoOrTerminato = stato === 'venduto' || stato === 'terminato';

  // Verifica se l'utente è il proprietario dell'oggetto
  const isOwner =
    isAuthenticated &&
    userData &&
    venditoreInfo.email &&
    userData.email === venditoreInfo.email;

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Pulsanti header: indietro e modifica (se owner) */}
        <HStack justify="space-between" align="center">
          <Box
            as="div"
            display="inline-block"
            bg="gray.100"
            py={2}
            px={3}
            borderRadius="md"
          >
            <Button
              leftIcon={<FaArrowLeft />}
              variant="ghost"
              size="sm"
              onClick={() => navigate('/bazar')}
            >
              Torna al Bazar
            </Button>
          </Box>
          {isOwner && (
            <IconButton
              icon={<FaEdit />}
              aria-label="Modifica oggetto"
              colorScheme="brand"
              onClick={() => navigate(`/bazar/${id}/modifica`)}
            />
          )}
        </HStack>

        {/* Grid principale: titolo + galleria a sinistra, info a destra */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
          {/* Sezione sinistra: Titolo sopra immagine */}
          <Box>
            <Box
              bg={cardBg}
              borderRadius="lg"
              p={4}
              border="1px solid"
              borderColor={borderColor}
              mb={4}
              textAlign="center"
            >
              <Heading size="lg">{titolo}</Heading>
            </Box>
            <BazarGallery bazar={bazar} />
          </Box>

          {/* Sezione destra: Informazioni */}
          <VStack spacing={6} align="stretch">
            {/* Prezzo, Venditore e Contatto su una sola riga (sopra i tag) */}
            <Box
              bg={cardBg}
              borderRadius="lg"
              p={4}
              border="1px solid"
              borderColor={borderColor}
            >
              <HStack
                spacing={6}
                align="flex-start"
                flexWrap="wrap"
                justify="center"
                divider={<StackDivider borderColor={borderColor} />}
              >
                {/* Prezzo */}
                <Box
                  flex="1"
                  minW={{ base: '100%', md: '120px' }}
                  textAlign="center"
                >
                  <Text
                    fontSize="xs"
                    color={useColorModeValue('gray.500', 'gray.400')}
                    mb={1}
                  >
                    Prezzo
                  </Text>
                  {prezzo !== null && prezzo !== undefined ? (
                    parseFloat(prezzo) === 0 ? (
                      <Badge colorScheme="green" fontSize="md" px={3} py={1}>
                        Regalo!
                      </Badge>
                    ) : (
                      <HStack spacing={2} flexWrap="wrap" justify="center">
                        <HStack spacing={1}>
                          <Icon as={FaEuroSign} />
                          <Text fontSize="xl" fontWeight="bold">
                            {parseFloat(prezzo).toFixed(2)}
                          </Text>
                        </HStack>
                        {prezzoNegoziabile && (
                          <Badge colorScheme="orange" fontSize="xs">
                            Negoziabile
                          </Badge>
                        )}
                      </HStack>
                    )
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      Non specificato
                    </Text>
                  )}
                </Box>

                {/* Venditore */}
                <Box
                  flex="1"
                  minW={{ base: '100%', md: '140px' }}
                  textAlign="center"
                >
                  <Text
                    fontSize="xs"
                    color={useColorModeValue('gray.500', 'gray.400')}
                    mb={1}
                  >
                    Venditore
                  </Text>
                  <Text fontWeight="medium">{venditoreInfo.nome}</Text>
                </Box>

                {/* Contatto */}
                <Box
                  flex="1"
                  minW={{ base: '100%', md: '160px' }}
                  textAlign="center"
                >
                  <Text
                    fontSize="xs"
                    color={useColorModeValue('gray.500', 'gray.400')}
                    mb={1}
                  >
                    Contatto
                  </Text>
                  {preferenzeContatto ? (
                    <VStack spacing={1} align="center">
                      {preferenzeContatto === 'telefono' && telefono && (
                        <HStack spacing={2} justify="center">
                          <Icon as={FaPhone} boxSize={3} />
                          <Text fontSize="sm">{telefono}</Text>
                        </HStack>
                      )}
                      {preferenzeContatto === 'mail' && venditoreInfo.email && (
                        <HStack spacing={2} justify="center">
                          <Icon as={FaEnvelope} boxSize={3} />
                          <Text
                            fontSize="sm"
                            isTruncated
                            title={venditoreInfo.email}
                          >
                            {venditoreInfo.email}
                          </Text>
                        </HStack>
                      )}
                      {preferenzeContatto === 'sede' && (
                        <HStack spacing={2} justify="center" align="start">
                          <Icon as={FaMapMarkerAlt} boxSize={3} mt={0.5} />
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="medium">
                              Sede CIL
                            </Text>
                            <Text
                              fontSize="xs"
                              color={useColorModeValue('gray.600', 'gray.400')}
                            >
                              Via Carlo Alberto 7, Chieri
                            </Text>
                          </VStack>
                        </HStack>
                      )}
                    </VStack>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      Non specificato
                    </Text>
                  )}
                </Box>
              </HStack>
            </Box>

            {/* Badge: stato, categoria, condizione */}
            <Box
              bg={cardBg}
              borderRadius="lg"
              p={4}
              border="1px solid"
              borderColor={borderColor}
            >
              <HStack spacing={2} flexWrap="wrap">
                <Badge
                  colorScheme={getStatoColor(stato)}
                  fontSize="sm"
                  px={3}
                  py={1}
                >
                  {getStatoLabel(stato)}
                </Badge>
                {categoria && (
                  <Badge colorScheme="blue" fontSize="sm" px={3} py={1}>
                    {categoria}
                  </Badge>
                )}
                {condizione && (
                  <Badge colorScheme="purple" fontSize="sm" px={3} py={1}>
                    {condizione}
                  </Badge>
                )}
              </HStack>
            </Box>

            {/* Descrizione */}
            {descrizione && (
              <Box
                bg={cardBg}
                borderRadius="lg"
                p={4}
                border="1px solid"
                borderColor={borderColor}
              >
                <Heading size="sm" mb={2}>
                  Descrizione
                </Heading>
                <Text whiteSpace="pre-wrap">{descrizione}</Text>
              </Box>
            )}

            {/* Stati speciali */}
            {isPrenotato && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Text fontWeight="medium">Oggetto attualmente prenotato</Text>
              </Alert>
            )}

            {isVendutoOrTerminato && (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontWeight="medium">Oggetto non più disponibile</Text>
              </Alert>
            )}

            {/* Nota fissa */}
            <Alert status="info" borderRadius="md" variant="subtle">
              <AlertIcon />
              <Text fontSize="sm">
                CIL non gestisce pagamenti o spedizioni. L&apos;accordo avviene
                direttamente tra i soci.
              </Text>
            </Alert>
          </VStack>
        </SimpleGrid>
      </VStack>
    </Container>
  );
}
